import {
  NavigationBreadcrumbComputed,
  NavigationRouterReducerState,
  PathRouter,
  replaceUrl,
  ROUTE_PARAM_ENTITY_ID,
  ROUTE_PARAM_HEADQUARTER_ID,
  ROUTE_PARAM_OFFER_ID,
  StaticPathRouter,
} from '@elio/emanager-front/shared/tools/router';
import { toNumberOrUndefined, toStringOrUndefined } from '@elio/shared/utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';

const ROUTER_KEY = 'router';

const getRouterState = createFeatureSelector<NavigationRouterReducerState>(ROUTER_KEY);

const getRouteQueryParams = createSelector(getRouterState, (routerState) => routerState?.state?.queryParams);

const getRouteParams = createSelector(getRouterState, (routerState) => routerState?.state?.routeParams);

const getRouteUrl = createSelector(getRouterState, (routerState) => routerState?.state?.url);

const getOfferIdFromRouteParam = createSelector(getRouteParams, (routeParams) =>
  toNumberOrUndefined(routeParams?.[ROUTE_PARAM_OFFER_ID])
);

const getHeadquarterIdFromRouteParam = createSelector(getRouteParams, (routeParams) =>
  toNumberOrUndefined(routeParams?.[ROUTE_PARAM_HEADQUARTER_ID])
);

const getEntityIdFromRouteParam = createSelector(getRouteParams, (routeParams) =>
  toNumberOrUndefined(routeParams?.[ROUTE_PARAM_ENTITY_ID])
);

const getStringEntityIdFromRouteParam = createSelector(getRouteParams, (routeParams) =>
  toStringOrUndefined(routeParams?.[ROUTE_PARAM_ENTITY_ID])
);

const getRouteQueryParam = <T>(param: string) =>
  createSelector(getRouteQueryParams, (queryParams) => {
    return queryParams[param] as T;
  });

const getRouteQueryParamAsNumber = (param: string) =>
  createSelector(getRouteQueryParams, (queryParams) => toNumberOrUndefined(queryParams[param]));

const getState = createSelector(getRouterState, (state: NavigationRouterReducerState) => state);

const getHeadquarterStartingUrl = createSelector(getRouteParams, (routeParams): PathRouter[] => {
  const headquarterId = toNumberOrUndefined(routeParams[ROUTE_PARAM_HEADQUARTER_ID]);
  return !headquarterId ? ['/'] : ['/', 'headquarter', headquarterId];
});

const getNavigationBreadcrumbs = createSelector(
  getRouterState,
  getOfferIdFromRouteParam,
  getEntityIdFromRouteParam,
  getRouteQueryParams,
  getHeadquarterStartingUrl,
  (
    routerState: NavigationRouterReducerState,
    offerId,
    entityId,
    queryParams,
    headquarterUrl
  ): NavigationBreadcrumbComputed[] =>
    routerState?.state.navigationBreadcrumbs.map((b) => ({
      ...b,
      routerLink: [...headquarterUrl, ...replaceUrl(b.routerLink, offerId, entityId)],
      labelParam: b.labelParamKey ? { [b.labelParamKey]: queryParams[b.labelParamKey] } : undefined,
    }))
);

const getUrlWithReplacedParams = (url: StaticPathRouter[]) =>
  createSelector(
    getOfferIdFromRouteParam,
    getEntityIdFromRouteParam,
    getHeadquarterStartingUrl,
    (offerId, entityId, headquarterUrl): PathRouter[] => [...headquarterUrl, ...replaceUrl(url, offerId, entityId)]
  );

export const RouterSelectors = {
  ROUTER_KEY,
  getRouterState,
  getRouteUrl,
  getOfferIdFromRouteParam,
  getHeadquarterIdFromRouteParam,
  getEntityIdFromRouteParam,
  getRouteQueryParams,
  getRouteQueryParam,
  getRouteQueryParamAsNumber,
  getHeadquarterStartingUrl,
  getState,
  getNavigationBreadcrumbs,
  getUrlWithReplacedParams,
  getStringEntityIdFromRouteParam,
};
